import React, { FC, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { apiGeneratePdfFromHtml } from 'api/adcritterApi'
import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { RootState } from 'app/rootReducer'
import { downloadBlobAsPdf, extractHtmlWithStyles } from 'utils/htmlExtractor'

import { selectEditMode } from '../masterCampaignPlanSlice'
import MarketAnalysisMarkdownRenderer from './MarketAnalysisMarkdownRenderer'
import TabPageHeader from './TabPageHeader'

export interface MarketAnalysisProps {
  masterCampaignPlan: MasterCampaignPlan
  onEditClick?: () => void
  printRef?: React.RefObject<HTMLDivElement>
  onAllClientPdfsClick?: () => void
  onAllStaffPdfsClick?: () => void
  staffPdfDisabledReason?: string
}

const MarketAnalysis: FC<MarketAnalysisProps> = ({ masterCampaignPlan, onEditClick, printRef: externalPrintRef, onAllClientPdfsClick, onAllStaffPdfsClick, staffPdfDisabledReason }) => {
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const editMode = useSelector(selectEditMode)

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

  const internalPrintRef = useRef<HTMLDivElement>(null)
  const printRef = externalPrintRef || internalPrintRef

  const generateServerSidePdf = async () => {
    if (!printRef.current || !currentAccount) {
      return
    }

    setIsGeneratingPdf(true)

    try {
      const htmlContent = await extractHtmlWithStyles(printRef.current, {
        pageTitle: `${masterCampaignPlan.name} - Market Analysis`,
        includeStyles: true,
        sanitize: true,
        prependCustomStyles: `
          * {
              box-sizing: border-box;
          }
          body {
              margin: 0;
              padding: 0;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              line-height: 1.6;
              color: #333;
          }
          html,body :not(thead):not(hr) {
              background: white !important;
              background-color: white !important;
          }
          img {
              max-width: 100%;
              height: auto;
          }
          table {
              border-collapse: collapse;
              width: 100%;
          }
          th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
          }
          @media print {
              body {
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
              }
          }`
      })

      const fileName = `${masterCampaignPlan.name}_Market_Analysis.pdf`

      const pdfBlob = await apiGeneratePdfFromHtml(fileName, htmlContent, 0, 0, 0, 0)

      downloadBlobAsPdf(pdfBlob, fileName)
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  if (!currentAccount) {
    return null
  }

  return (
    <>
      <TabPageHeader title='' onClientPdfClick={generateServerSidePdf} onEditClick={editMode ? onEditClick : undefined} isGeneratingPdf={isGeneratingPdf} onAllClientPdfsClick={onAllClientPdfsClick} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} usePencilIconForEdit />

      <div id='pdf-content' ref={printRef}>
        {masterCampaignPlan.marketAnalysisContent && <MarketAnalysisMarkdownRenderer content={masterCampaignPlan.marketAnalysisContent} />}
      </div>
    </>
  )
}

export default MarketAnalysis
